import Phaser from 'phaser';
import { SCENE, STREET_SPAWNS, STREET_SPAWN_Y } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite, NATALKA_SIZE } from '../ui/NatalkaSprite.js';
import { reportLocation } from '../ui/locationTracker.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { attachSceneEditMode } from '../ui/SceneEditMode.js';
import { realtime } from '../api/realtime.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { applyForeground } from './sceneForeground.js';
import { fitCharacter } from '../ui/outdoorScale.mjs';
import { ROOM_PERSPECTIVE } from './roomPerspective.mjs';
import { ySortDepth } from '../ui/ySort.js';
import { virtualInput } from '../ui/virtualInput.js';
import { isInputLocked } from '../ui/inputLock.js';
import { showGameShopModal } from '../ui/GameShopModal.js';
import { showRankingModal } from '../ui/RankingModal.js';
import { showToast } from '../ui/Toast.js';
import { GameHallTables } from '../ui/GameHallTables.js';
import {
  DOWN_FLOOR, DOWN_OBSTACLES, DOWN_EXIT_DOOR, DOWN_STAIRS_ZONE, DOWN_COUNTER_ZONE, DOWN_TABLES,
  DOWN_RANKING_ZONE,
  feetInAnyRect, rectContains,
} from './gameHallLayout.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { renderBackground } from '../render/world.js';

const HALF_H = NATALKA_SIZE.h / 2;
const ROOM = 'game_hall:down';
// Street-side world x of the hall door — where we respawn on exit (see streetSpawns.mjs).
const STREET_DOOR_X = STREET_SPAWNS.GAME_HALL;

export class GameHallScene extends Phaser.Scene {
  constructor() { super(SCENE.GAME_HALL); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    this._spawn = { x: data?.spawnX ?? 180, y: data?.spawnY ?? 600 };
    // Saved positions can land inside obstacle rects after geometry refinements — rescue
    // to the scene default instead of softlocking (WalkController has no unstick).
    const FALLBACK = { x: 180, y: 600 };
    if (feetInAnyRect(DOWN_OBSTACLES, this._spawn.x, this._spawn.y + NATALKA_SIZE.h / 2)) {
      this._spawn = { ...FALLBACK };
    }
    renderBackground(this, 'bg_game_hall_down');

    this.perspective = ROOM_PERSPECTIVE.game_hall;   // smaller and slower towards the far edge
    this.natalka = new NatalkaSprite(this, this._spawn.x, this._spawn.y);
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.walker = new WalkController(this, this.natalka, {
      floorPolyline: DOWN_FLOOR,
      blocked: (fx, fy) => feetInAnyRect(DOWN_OBSTACLES, fx, fy),
      onBroadcast: (p) => realtime.move(p),
    });
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });

    // Re-drawn bg polygons that occlude avatars standing behind furniture
    // (FOREGROUND_BY_SCENE['game_hall_down'] — authored in Task 5).
    this._fg = applyForeground(this, 'game_hall_down', 'bg_game_hall_down');
    this.events.on('shutdown', () => this._fg?.destroy());

    new MoneyBar(this);
    attachFriendsButton(this);

    this._unsub = gameState.on(() => this.natalka?.setOutfit(gameState.raw?.inventory?.equipped));
    this.events.on('shutdown', () => this._unsub?.());

    attachCoordsDebug(this);
    if (import.meta.env?.DEV) attachSceneEditMode(this);  // press E: author floor/foreground

    this._presence = attachRemotePresence(this, ROOM);
    this._roomChat = attachRoomChat(this, ROOM);
    this.tablesCtl = new GameHallTables(this, { tables: DOWN_TABLES, presence: this._presence, floorRoom: ROOM });
    if (import.meta.env?.DEV) {
      // Press G: hall authoring tool (seat anchors, felt boxes, per-game board overrides, counter zone).
      import('../ui/GameHallEditMode.js').then(({ attachGameHallEditMode }) =>
        attachGameHallEditMode(this, {
          tables: DOWN_TABLES, counterZone: DOWN_COUNTER_ZONE, floor: 'down',
          obstacles: DOWN_OBSTACLES, floorLine: DOWN_FLOOR,
        }));
    }

    // Don't re-trigger the exit door / stairs right after spawning next to them.
    this._zoneGuardUntil = Date.now() + 600;
    this._exitTriggered = false;

    // Desktop affordance for the counter action (the SPACE key doubles as "use").
    this._spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    // Clickable counter (same interaction as chairs): hand cursor over the counter,
    // click opens the shop when close enough. The zone covers the counter's visual
    // front (its obstacle rect ~x260-580, base ~y300-455 plus the desk face above).
    this._counterZone = this.add.zone(427, 361, 310, 132).setInteractive({ useHandCursor: true });
    this._counterZone.on('pointerup', () => {
      if (isInputLocked(this)) return;
      const fx = this.natalka.x, fy = this.natalka.feetY;
      if (rectContains(DOWN_COUNTER_ZONE, fx, fy) ||
          Math.hypot(fx - 423, fy - 480) < 220) {
        showGameShopModal(this);
      } else {
        showToast(this, 'Podejdź do lady {dice}');
      }
    });

    // Ranking board: the framed "HOUSE RULES" easel by the counter (DOWN_RANKING_ZONE)
    // — same click idiom as the counter/chairs (hand cursor, proximity-gated).
    const rankCx = DOWN_RANKING_ZONE.x + DOWN_RANKING_ZONE.w / 2;
    const rankCy = DOWN_RANKING_ZONE.y + DOWN_RANKING_ZONE.h / 2;
    this._rankingZone = this.add.zone(rankCx, rankCy, DOWN_RANKING_ZONE.w, DOWN_RANKING_ZONE.h)
      .setInteractive({ useHandCursor: true });
    this._rankingZone.on('pointerup', () => {
      if (isInputLocked(this)) return;
      const fx = this.natalka.x, fy = this.natalka.feetY;
      if (Math.hypot(fx - 423, fy - 480) < 220) {
        showRankingModal(this);
      } else {
        showToast(this, 'Podejdź bliżej tablicy {trophy}');
      }
    });

    this.events.on('shutdown', () => {
      if (virtualInput.getAction()?.label === 'Kup gry {dice}') virtualInput.clearAction();
    });
  }

  update(_t, dt) {
    this.walker.update(dt);
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y);
    this._depthSort();

    const fx = this.natalka.x, fy = this.natalka.feetY;

    // Counter: offer "Kup gry" while standing at the counter front. While input is
    // locked (shop modal open), clear our own action first so the mobile button
    // doesn't sit over the modal re-invoking showGameShopModal (mirrors BikeController's
    // syncBikeAction).
    const atCounter = rectContains(DOWN_COUNTER_ZONE, fx, fy);
    if (isInputLocked(this)) {
      const a = virtualInput.getAction();
      if (a && a.label === 'Kup gry {dice}') virtualInput.clearAction();
    } else {
      if (atCounter && virtualInput.getAction()?.label !== 'Kup gry {dice}') {
        virtualInput.registerAction('Kup gry {dice}', () => showGameShopModal(this));
      } else if (!atCounter && virtualInput.getAction()?.label === 'Kup gry {dice}') {
        virtualInput.clearAction();
      }
    }
    // Seats / nameplates / tabletop games + sit/stand/host action (registers its own
    // virtualInput action; label-guards keep it from stomping the counter action above).
    // The counter zone overlaps t1's seat radius — suppress the sit offer there so
    // "Kup gry 🎲" wins in the wedge instead of the two blocks fighting every frame.
    this.tablesCtl.update(fx, fy, { suppressSit: atCounter });

    // Desktop SPACE invokes whichever action is currently offered (counter buy or seat/stand/menu).
    if (Phaser.Input.Keyboard.JustDown(this._spaceKey) && !isInputLocked(this) && virtualInput.getAction()) {
      virtualInput.invokeAction();
    }

    if (this._exitTriggered || Date.now() < this._zoneGuardUntil) return;

    if (rectContains(DOWN_EXIT_DOOR, fx, fy)) {
      this._exitTriggered = true;
      this.scene.start(SCENE.OUTSIDE, { spawnX: STREET_DOOR_X, spawnY: STREET_SPAWN_Y, upExit: this.walker.walkingUp });
      return;
    }
    if (rectContains(DOWN_STAIRS_ZONE, fx, fy)) {
      this._exitTriggered = true;
      this.scene.start(SCENE.GAME_HALL_UP, { spawnX: 340, spawnY: 580 });
    }
  }

  _depthSort() {
    this.natalka.setDepth(ySortDepth(this.natalka.feetY));
    if (this._presence) {
      for (const a of this._presence.avatars.values()) {
        a.sprite.setDepth(ySortDepth((a.sprite.feetY ?? a.sprite.y + HALF_H)));
      }
    }
  }
}
